import { cn, tv } from "tailwind-variants";
import type { ApiBlockGridModel } from "#/_generated/umbraco/delivery-api/models";
import type { CSSProperties } from "react";
import { BlockGrid } from "#/_packages/umbrella/integrations/umbraco/property-components/block-grid.tsx";

export type NestedLayoutDisplay = "grid" | "flex";
export type NestedLayoutFlexDirection =
	| "row"
	| "row-reverse"
	| "column"
	| "column-reverse";
export type NestedLayoutFlexWrap = "nowrap" | "wrap" | "wrap-reverse";
export type NestedLayoutJustifyContent =
	| "start"
	| "center"
	| "end"
	| "space-between"
	| "space-around"
	| "space-evenly";
export type NestedLayoutAlignItems =
	| "start"
	| "center"
	| "end"
	| "stretch"
	| "baseline";
export type NestedLayoutBoxVariant =
	| "none"
	| "background"
	| "outline"
	| "shadow";
export type NestedLayoutRounded =
	| "default"
	| "none"
	| "xs"
	| "sm"
	| "md"
	| "lg"
	| "xl"
	| "full";

export type NestedLayoutProps = {
	id?: string;
	display?: NestedLayoutDisplay;
	flexDirection?: NestedLayoutFlexDirection;
	flexWrap?: NestedLayoutFlexWrap;
	justifyContent?: NestedLayoutJustifyContent;
	alignItems?: NestedLayoutAlignItems;
	columnGap?: number;
	rowGap?: number;
	boxVariant?: NestedLayoutBoxVariant;
	rounded?: NestedLayoutRounded;
	animationScrollType?: string | null;
	hoverItemClasses?: string | null;
	className?: string;
	style?: CSSProperties;
	blockGrid?: ApiBlockGridModel | undefined;
	alignmentHorizontal?: "start" | "center" | "end" | undefined;
	alignmentSelfVertical?: "stretch" | "start" | "center" | "end" | undefined;
};

const nestedLayout = tv({
	base: "block__nested-layout w-full",
	variants: {
		display: {
			grid: "grid",
			flex: "_block-grid-flex flex",
		},
		boxVariant: {
			none: "",
			background: "card card-body card-background",
			outline: "card card-body card-outline",
			shadow: "card card-body card-shadow",
		},
		rounded: {
			default: "",
			none: "rounded-0",
			xs: "rounded-(--umb-border-radius-xs)",
			sm: "rounded-(--umb-border-radius-sm)",
			md: "rounded-(--umb-border-radius-md)",
			lg: "rounded-(--umb-border-radius-lg)",
			xl: "rounded-(--umb-border-radius-xl)",
			full: "rounded-(--umb-border-radius-pill)",
		},
		justifyContent: {
			start: "justify-start",
			center: "justify-center",
			end: "justify-end",
			"space-between": "justify-between",
			"space-around": "justify-around",
			"space-evenly": "justify-evenly",
		},
		alignItems: {
			start: "items-start",
			center: "items-center",
			end: "items-end",
			stretch: "items-stretch",
			baseline: "items-baseline",
		},
		alignmentHorizontal: {
			start: "justify-start",
			center: "justify-center",
			end: "justify-end",
		},
		alignmentSelfVertical: {
			stretch: "",
			start: "self-start",
			center: "self-center",
			end: "self-end",
		},
		flexDirection: {
			row: "flex-row",
			"row-reverse": "flex-row-reverse",
			column: "flex-col",
			"column-reverse": "flex-col-reverse",
		},
		flexWrap: {
			nowrap: "flex-nowrap",
			wrap: "flex-wrap",
			"wrap-reverse": "flex-wrap-reverse",
		},
	},
});

export function NestedLayout({
	display = "grid",
	flexDirection,
	flexWrap,
	justifyContent,
	alignItems = "stretch",
	columnGap,
	rowGap,
	boxVariant = "none",
	rounded = "default",
	animationScrollType,
	hoverItemClasses,
	className,
	style,
	blockGrid,
}: NestedLayoutProps) {
	const gapStyle = {
		columnGap:
			typeof columnGap === "number" ? `${Math.max(0, columnGap)}px` : undefined,
		rowGap: typeof rowGap === "number" ? `${Math.max(0, rowGap)}px` : undefined,
	};

	return (
		<BlockGrid
			noVerticalSpacing={false}
			className={cn(
				nestedLayout({
					display,
					flexDirection: display === "flex" ? flexDirection : undefined,
					flexWrap: display === "flex" ? flexWrap : undefined,
					justifyContent: display === "flex" ? justifyContent : undefined,
					alignItems: display === "flex" ? alignItems : undefined,
					boxVariant,
					rounded,
				}),
				hoverItemClasses,
				animationScrollType,
				className,
			)}
			style={{ ...gapStyle, ...style }}
			contentArea={blockGrid}
		/>
	);
}
